import { Box, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { visuallyHidden } from "@mui/utils";

const PHASES = [
  ["preparation", "Προετοιμασία εκτίμησης", "Preparing estimate"],
  ["weather", "Προετοιμασία μετεωρολογικών δεδομένων", "Preparing weather data"],
  ["production", "Υπολογισμός παραγωγής και απωλειών", "Calculating generation and losses"],
  ["storage", "Αποθήκευση αποτελεσμάτων", "Saving results"],
];

// Waiting this long for the worker is no longer the usual few seconds.
const LONG_WAIT_MS = 90 * 1000;

/** Segments represent actual phases, not percentages or equal durations. */
export default function EstimationProgress({ job, isGreek }) {
  const completed = job?.status === "completed";
  const running = job?.status === "running";
  const queued = job?.status === "queued";
  const phase = running ? PHASES.findIndex(([key]) => key === job.message) : -1;
  // Before a phase starts nothing is lit, so the wait is said in words: the
  // request on its way, then the job in the queue until the estimation worker
  // takes it (it runs one job at a time). "Preparing estimate" is the first
  // thing the worker does, not the wait.
  const waiting = !completed && phase < 0;
  const queuedFor = queued && job?.created_at ? Date.now() - new Date(job.created_at).getTime() : 0;
  const label = completed
    ? (isGreek ? "Η εκτίμηση ολοκληρώθηκε" : "Estimate completed")
    : phase >= 0 ? PHASES[phase][isGreek ? 1 : 2]
      : queued ? (isGreek ? "Σε αναμονή εκτέλεσης…" : "Waiting to start…")
        : (isGreek ? "Έναρξη εκτίμησης…" : "Starting estimate…");
  const longWait = queued && queuedFor > LONG_WAIT_MS
    ? (isGreek
      ? " Ο υπολογιστής εκτιμήσεων εκτελεί μία εργασία τη φορά· είτε ολοκληρώνει άλλη εργασία είτε δεν τρέχει αυτή τη στιγμή."
      : " The estimation worker runs one job at a time: it is either finishing another job or not running right now.")
    : "";

  return (
    <Box sx={{ width: "100%", py: 1, position: "relative", pr: 4, boxSizing: "border-box" }}>
      <Box aria-label={isGreek ? "Στάδια εκτίμησης" : "Estimation stages"}
        sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", columnGap: { xs: 0.75, sm: 1.25 }, rowGap: 1, alignItems: "end" }}>
        {PHASES.map(([key, el, en], index) => (
          <Typography key={key} variant="caption" sx={{ fontSize: { xs: 10, sm: 12 }, lineHeight: 1.4,
            color: completed ? "success.main" : index === phase ? "primary.main" : "text.secondary",
            fontWeight: completed || index === phase ? 700 : 500 }}>
            {isGreek ? el : en}
          </Typography>
        ))}
        {PHASES.map(([key, el, en], index) => {
          const done = completed || index < phase;
          const current = !completed && index === phase;
          return <Box key={key} title={isGreek ? el : en} aria-current={current ? "step" : undefined}
            data-state={done ? "completed" : current ? "active" : "upcoming"}
            sx={{ height: 12, borderRadius: 2, overflow: "hidden", position: "relative",
              bgcolor: completed ? "success.main" : done || current ? "primary.main" : "action.disabledBackground",
              transition: "background-color 250ms ease",
              ...(current && {
                "&::after": { content: '""', position: "absolute", inset: 0,
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,.55), transparent)",
                  animation: "estimationSweep 1.6s linear infinite" },
                "@keyframes estimationSweep": { from: { transform: "translateX(-100%)" }, to: { transform: "translateX(100%)" } },
                "@media (prefers-reduced-motion: reduce)": { "&::after": { animation: "none" } },
              }),
            }} />;
        })}
      </Box>
      {completed && <CheckCircleRoundedIcon aria-label={isGreek ? "Ολοκληρώθηκε" : "Completed"}
        sx={{ position: "absolute", right: 0, bottom: 3, fontSize: 24, color: "success.main" }} />}
      <Typography role="status" aria-live="polite" variant="caption"
        sx={waiting ? { display: "block", mt: 0.75, color: "text.secondary" } : visuallyHidden}>
        {label}{waiting ? longWait : ""}
      </Typography>
    </Box>
  );
}
